import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import { addressPropType } from './address-prop-type';
import { FIELD_NAMES } from './constants';
import { AddressFormContextProvider } from './context';
import { CountryFields } from './country-fields';
import style from './style.module.less';

/** @typedef {typeof import('./constants').FIELD_NAMES[keyof typeof import('./constants').FIELD_NAMES]} FieldNames */

/**
 * @param {object} props
 * @param {object} props.address
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.disableEmail]
 * @param {object} [props.errors]
 * @param {boolean} [props.hideOptionalLabels]
 * @param {boolean} [props.line3]
 * @param {boolean} [props.state]
 * @param {boolean} [props.showAllErrors]
 * @param {'v2' | 'v3'} [props.type]
 * @param {boolean} [props.isFormDescendant]
 * @param {Partial<Record<FieldNames, string>>} [props.subLabels]
 * @param {(fieldName: string, value: any) => void} [props.onChange]
 * @param {(fieldName: string, value: any) => void} [props.trackInputChange]
 * @param {(fieldName: string) => void} [props.trackInputClick]
 * @param {FieldNames[]} [props.hiddenFields]
 * @param {FieldNames[]} [props.overrideRequiredFields]
 * @param {FieldNames[]} [props.additionalRequiredFields]
 * @returns {JSX.Element}
 */
export const AddressFields = ({
  additionalRequiredFields = emptyArray,
  address,
  disabled = false,
  disableEmail = false,
  errors = emptyObject,
  hideOptionalLabels = false,
  line3 = false,
  state = false,
  hiddenFields = emptyArray,
  onChange = noop,
  showAllErrors = false,
  subLabels = emptyObject,
  trackInputChange = noop,
  trackInputClick = noop,
  type = '',
  isFormDescendant = false,
  overrideRequiredFields = null,
}) => {
  const classes = classnames(style['address-fields'], { [style['v2-inputs']]: type === 'v2' }, { [style['v3-inputs']]: type === 'v3' });

  // forms can't be nested, so if this component is already inside a form, we need to use a div instead
  const ContainerElement = isFormDescendant ? 'div' : 'form';

  return (
    <AddressFormContextProvider
      additionalRequiredFields={additionalRequiredFields}
      address={address}
      disabled={disabled}
      disableEmail={disableEmail}
      errors={errors}
      hiddenFields={hiddenFields}
      hideOptionalLabels={hideOptionalLabels}
      line3={line3}
      onChange={onChange}
      overrideRequiredFields={overrideRequiredFields}
      showAllErrors={showAllErrors}
      state={state}
      subLabels={subLabels}
      trackInputChange={trackInputChange}
      trackInputClick={trackInputClick}
      type={type}
    >
      <ContainerElement className={classes}>
        <CountryFields />
      </ContainerElement>
    </AddressFormContextProvider>
  );
};

AddressFields.propTypes = {
  address: addressPropType.isRequired,
  onChange: PropTypes.func,
  additionalRequiredFields: PropTypes.arrayOf(PropTypes.oneOf(Object.values(FIELD_NAMES))),
  hiddenFields: PropTypes.arrayOf(PropTypes.oneOf(Object.values(FIELD_NAMES))),
  hideOptionalLabels: PropTypes.bool,
  line3: PropTypes.bool,
  state: PropTypes.bool,
  errors: PropTypes.object,
  showAllErrors: PropTypes.bool,
  disabled: PropTypes.bool,
  disableEmail: PropTypes.bool,
  type: PropTypes.string,
  trackInputChange: PropTypes.func,
  trackInputClick: PropTypes.func,
  isFormDescendant: PropTypes.bool,
  subLabels: PropTypes.object,
  overrideRequiredFields: PropTypes.arrayOf(PropTypes.oneOf(Object.values(FIELD_NAMES))),
};
